import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, FileText, Trash2, Download, RefreshCw,
  CheckCircle, Clock, XCircle, Sparkles, Loader2,
  CheckCircle2, XCircle as XCircle2, AlertTriangle, HelpCircle,
} from 'lucide-react';
import { CollapsibleCard } from '../../components/ui/collapsible-card';
import apiClient, { api } from '../../core/apiClient';
import TenderAnalysisReport, { type TenderAnalysisData } from './TenderAnalysisReport';

interface CriterionResult {
  key: string;
  label: string;
  status: 'PASS' | 'FAIL' | 'WARNING' | 'UNKNOWN';
  tenderValue: string | null;
  threshold: string | null;
  reason: string;
}

const CRITERION_CONFIG: Record<string, { icon: any; badge: string; badgeClass: string }> = {
  PASS:    { icon: CheckCircle2,  badge: 'Passa',          badgeClass: 'bg-green-100 text-green-700' },
  FAIL:    { icon: XCircle2,      badge: 'No passa',       badgeClass: 'bg-red-100 text-red-700' },
  WARNING: { icon: AlertTriangle, badge: 'Revisió manual', badgeClass: 'bg-amber-100 text-amber-700' },
  UNKNOWN: { icon: HelpCircle,    badge: 'Sense dades',    badgeClass: 'bg-muted text-muted-foreground' },
};

interface TenderDocument {
  id: string;
  docType: 'PCAP' | 'PPT' | 'MEMORIA' | 'ALTRES';
  originalFilename: string;
  fileSize: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  PCAP: 'PCAP (Plec administratiu)',
  PPT: 'PPT (Plec tècnic)',
  MEMORIA: 'Memòria justificativa',
  ALTRES: 'Altres',
};

const STATUS_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-100', label: 'Pendent' },
  processing: { icon: RefreshCw, color: 'text-blue-600 bg-blue-100 animate-spin', label: 'Processant' },
  completed: { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Completat' },
  failed: { icon: XCircle, color: 'text-red-600 bg-red-100', label: 'Error' },
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TenderAnalysisPanel({ tenderId }: { tenderId: string }) {
  const [documents, setDocuments] = useState<TenderDocument[]>([]);
  const [docType, setDocType] = useState<'PCAP' | 'PPT' | 'MEMORIA' | 'ALTRES'>('PCAP');
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState<TenderAnalysisData | null>(null);
  const [criteriaResults, setCriteriaResults] = useState<CriterionResult[]>([]);
  const [generating, setGenerating] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: TenderDocument[] }>(`/tenders/${tenderId}/documents`);
      setDocuments(res.data);
    } catch (err) {
      console.error('[TenderAnalysisPanel] Error carregant documents:', err);
    }
  }, [tenderId]);

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: { data: TenderAnalysisData } | null }>(`/tenders/${tenderId}/analysis`);
      setAnalysis(res.data?.data ?? null);
    } catch (err) {
      console.error('[TenderAnalysisPanel] Error carregant informe:', err);
    }
  }, [tenderId]);

  const fetchCriteria = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: CriterionResult[] }>(`/tender-criteria/${tenderId}/evaluation`);
      setCriteriaResults(res.data ?? []);
    } catch {
      // criteria config pot no existir encara
    }
  }, [tenderId]);

  useEffect(() => {
    fetchDocuments();
    fetchAnalysis();
    fetchCriteria();
  }, [fetchDocuments, fetchAnalysis, fetchCriteria]);

  // Refresc automàtic mentre hi hagi documents en procés
  useEffect(() => {
    const hasPending = documents.some(d => d.status === 'pending' || d.status === 'processing');
    if (hasPending) {
      const interval = setInterval(fetchDocuments, 3000);
      return () => clearInterval(interval);
    }
  }, [documents, fetchDocuments]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docType', docType);

      await apiClient.post(`/tenders/${tenderId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await fetchDocuments();
    } catch (err) {
      console.error('[TenderAnalysisPanel] Error pujant document:', err);
      alert('Error en pujar el document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Estàs segur que vols eliminar aquest document?')) return;
    try {
      await api.delete(`/tenders/${tenderId}/documents/${docId}`);
      await fetchDocuments();
    } catch (err) {
      console.error('[TenderAnalysisPanel] Error eliminant document:', err);
    }
  };

  const handleDownloadPdf = async () => {
    const res = await apiClient.get(
      `/tenders/${tenderId}/analysis/pdf`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analisi-licitacio-${tenderId}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleGenerateAnalysis = async () => {
    setGenerating(true);
    setAnalysisError(null);
    try {
      const res = await api.post<{ success: boolean; data: { data: TenderAnalysisData } }>(`/tenders/${tenderId}/analysis`);
      setAnalysis(res.data.data);
      await fetchCriteria();
    } catch (err: any) {
      console.error('[TenderAnalysisPanel] Error generant informe:', err);
      setAnalysisError(err.response?.data?.message || err.response?.data?.error?.message || "Error en generar l'informe");
    } finally {
      setGenerating(false);
    }
  };

  const hasCompletedDocs = documents.some(d => d.status === 'completed');

  return (
    <div className="space-y-6">
      {/* Documents */}
      <CollapsibleCard title="Documents de la licitació" icon={FileText} iconClassName="text-teal-600" count={documents.length}>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value as typeof docType)}
              className="px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {uploading ? 'Pujant...' : 'Pujar document'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.tiff"
              onChange={handleUpload}
              className="hidden"
            />
          </div>

          {documents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Encara no s'ha pujat cap document (PCAP, PPT, Memòria justificativa...).
            </p>
          ) : (
            <div className="border rounded-lg divide-y">
              {documents.map((doc) => {
                const status = STATUS_CONFIG[doc.status];
                const StatusIcon = status.icon;
                return (
                  <div key={doc.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`flex items-center justify-center h-6 w-6 rounded-full ${status.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{doc.originalFilename}</p>
                        <p className="text-xs text-muted-foreground">
                          {DOC_TYPE_LABELS[doc.docType]} · {formatFileSize(doc.fileSize)} · {status.label}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={async () => {
                          const res = await apiClient.get(
                            `/tenders/${tenderId}/documents/${doc.id}/file`,
                            { responseType: 'blob' }
                          );
                          const url = URL.createObjectURL(res.data);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = doc.originalFilename;
                          a.click();
                          setTimeout(() => URL.revokeObjectURL(url), 5000);
                        }}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Descarregar"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors text-destructive"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Informe d'anàlisi */}
      <CollapsibleCard
        title="Informe d'anàlisi de la licitació"
        icon={Sparkles}
        iconClassName="text-amber-600"
        headerActions={
          <div className="flex items-center gap-2">
            {analysis && (
              <button
                type="button"
                onClick={handleDownloadPdf}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
              >
                <Download className="h-4 w-4" />
                Descarregar PDF
              </button>
            )}
            <button
              type="button"
              onClick={handleGenerateAnalysis}
              disabled={generating || !hasCompletedDocs}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              title={!hasCompletedDocs ? 'Cal pujar i processar almenys un document' : ''}
            >
              {generating && <Loader2 className="h-4 w-4 animate-spin" />}
              {analysis ? 'Actualitzar informe' : 'Generar informe'}
            </button>
          </div>
        }
      >
        <div className="p-5">
          {analysisError && (
            <p className="text-sm text-destructive mb-4">{analysisError}</p>
          )}
          {analysis ? (
            <div id="tender-analysis-print">
              <div className="print-header">
                <div>
                  <h2 className="text-sm font-bold">Informe d'anàlisi de licitació</h2>
                  <p className="text-xs text-muted-foreground mt-1">Generat el {new Date().toLocaleDateString('ca-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p className="font-semibold">ROIG I FILLS ASSOCIATS S.L.</p>
                  <p>Gestió Integral</p>
                </div>
              </div>

              {/* Secció 0: Avaluació prèvia */}
              {criteriaResults.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-1 text-sm">0. Avaluació prèvia — Criteris de valoració</h3>
                  <div className="flex gap-3 mb-3 text-xs">
                    {(() => {
                      const pass = criteriaResults.filter(r => r.status === 'PASS').length;
                      const fail = criteriaResults.filter(r => r.status === 'FAIL').length;
                      return (
                        <>
                          {pass > 0 && <span className="text-green-700 font-medium">{pass} criteris compleixen</span>}
                          {fail > 0 && <span className="text-red-700 font-medium">{fail} criteris no compleixen</span>}
                        </>
                      );
                    })()}
                  </div>
                  <div className="border rounded-lg divide-y">
                    {criteriaResults.map((r) => {
                      const cfg = CRITERION_CONFIG[r.status] ?? CRITERION_CONFIG.UNKNOWN;
                      const Icon = cfg.icon;
                      return (
                        <div key={r.key} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                          <div className="flex items-start gap-2 min-w-0">
                            <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <p className="font-medium text-xs">{r.label}</p>
                              <p className="text-xs text-muted-foreground">{r.reason}</p>
                              {(r.tenderValue || r.threshold) && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {r.tenderValue && <><span className="font-medium">Detectat:</span> {r.tenderValue}</>}
                                  {r.tenderValue && r.threshold && <span className="mx-2">·</span>}
                                  {r.threshold && <><span className="font-medium">Configurat:</span> {r.threshold}</>}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.badgeClass}`}>
                            {cfg.badge}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <TenderAnalysisReport data={analysis} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Encara no s'ha generat cap informe. Puja els documents oficials de la licitació (PCAP, PPT, Memòria) i prem "Generar informe".
            </p>
          )}
        </div>
      </CollapsibleCard>
    </div>
  );
}
